// Google Cloud Vision API 服務
export interface VisionOCRResult {
  text: string;
  confidence: number;
  pageCount: number;
  language?: string;
}

class GoogleVisionService {
  private apiKey: string;
  private baseUrl: string;

  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_VISION_API_KEY || '';
    this.baseUrl = 'https://vision.googleapis.com/v1/images:annotate';
    console.log('Google Vision API配置檢查:', {
      hasApiKey: !!this.apiKey,
      apiKeyLength: this.apiKey.length,
      isConfigured: this.isConfigured()
    });
  }

  // 檢查API配置
  isConfigured(): boolean {
    return !!this.apiKey && this.apiKey !== 'your_google_vision_api_key_here';
  }

  // 使用Vision API進行OCR
  async extractTextFromImage(imageData: string): Promise<VisionOCRResult> {
    if (!this.isConfigured()) {
      throw new Error('Google Vision API未配置，請設置REACT_APP_GOOGLE_VISION_API_KEY環境變量');
    }

    try {
      console.log('開始使用Google Vision API進行OCR...');
      
      const requestBody = {
        requests: [
          {
            image: {
              content: imageData
            },
            features: [
              {
                type: 'TEXT_DETECTION',
                maxResults: 1
              }
            ],
            imageContext: {
              languageHints: ['zh-TW', 'zh-CN', 'en']
            }
          }
        ]
      };

      const response = await fetch(`${this.baseUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Vision API錯誤: ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      const result = data.responses[0];

      if (result.error) {
        throw new Error(`Vision API處理錯誤: ${result.error.message}`);
      }

      if (!result.textAnnotations || result.textAnnotations.length === 0) {
        console.log('Vision API未檢測到文字');
        return {
          text: '',
          confidence: 0,
          pageCount: 1
        };
      }

      // 第一個註釋包含所有檢測到的文字
      const fullText = result.textAnnotations[0].description || '';
      const confidence = this.calculateConfidence(result.textAnnotations);

      console.log('Vision API OCR成功，文本長度:', fullText.length);
      console.log('檢測到的語言:', result.textAnnotations[0].locale || '未知');

      return {
        text: fullText,
        confidence: confidence,
        pageCount: 1,
        language: result.textAnnotations[0].locale
      };

    } catch (error: any) {
      console.error('Vision API OCR失敗:', error);
      throw new Error(`Vision API OCR失敗: ${error.message}`);
    }
  }

  // 計算OCR可信度
  private calculateConfidence(annotations: any[]): number {
    if (!annotations || annotations.length === 0) return 0;
    
    // 基於檢測到的文字塊數量估算可信度
    const textBlocks = annotations.length - 1; // 減去全文字註釋
    const baseConfidence = Math.min(textBlocks / 10, 0.9); // 最多90%可信度
    
    return Math.max(baseConfidence, 0.3); // 最少30%可信度
  }

  // 將PDF「單一頁面」轉換為圖片
  private async convertPDFPageToImage(pdf: any, pageNum: number): Promise<string> {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2.0 }); // 提高解析度

    // 創建canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // 渲染PDF頁面到canvas
    await page.render({
      canvasContext: context!,
      viewport: viewport,
      canvas: canvas
    }).promise;

    // 轉換為base64
    return canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
  }

  // 將PDF轉換為圖片（僅第一頁，保留給只需要單頁預覽的呼叫端使用）
  async convertPDFToImage(file: File): Promise<string> {
    try {
      console.log('開始將PDF轉換為圖片...');

      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const imageData = await this.convertPDFPageToImage(pdf, 1);

      console.log('PDF轉換為圖片成功');
      return imageData;

    } catch (error) {
      console.error('PDF轉圖片失敗:', error);
      throw new Error('PDF轉圖片失敗');
    }
  }

  // 主要方法：從PDF文件提取文字
  // 過去這裡只轉換並 OCR 第一頁，導致掃描件保單第 2 頁以後的內容
  // （通常正是承保範圍、給付表所在的位置）完全沒被辨識到。
  // 改成逐頁轉圖、逐頁 OCR，再把所有頁的文字串接起來。
  async extractTextFromPDF(file: File): Promise<VisionOCRResult> {
    try {
      console.log('開始使用Vision API處理PDF:', file.name);

      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const MAX_PAGES = 30; // 掃描件 OCR 每頁都要呼叫一次 Vision API，設上限避免超長文件耗時/耗費過多
      const pageCount = Math.min(pdf.numPages, MAX_PAGES);
      if (pdf.numPages > MAX_PAGES) {
        console.warn(`⚠️ PDF 共 ${pdf.numPages} 頁，超過 OCR 上限 ${MAX_PAGES} 頁，僅辨識前 ${MAX_PAGES} 頁`);
      }

      const pageTexts: string[] = [];
      const confidences: number[] = [];
      let detectedLanguage: string | undefined;

      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        console.log(`🔍 OCR 第 ${pageNum}/${pageCount} 頁...`);
        const imageData = await this.convertPDFPageToImage(pdf, pageNum);
        const pageResult = await this.extractTextFromImage(imageData);

        if (pageResult.text) {
          pageTexts.push(pageResult.text);
        }
        confidences.push(pageResult.confidence);
        if (!detectedLanguage && pageResult.language) {
          detectedLanguage = pageResult.language;
        }
      }

      const averageConfidence = confidences.length > 0
        ? confidences.reduce((sum, c) => sum + c, 0) / confidences.length
        : 0;

      return {
        text: pageTexts.join('\n\n'),
        confidence: averageConfidence,
        pageCount,
        language: detectedLanguage
      };

    } catch (error: any) {
      console.error('Vision API PDF處理失敗:', error);
      throw error;
    }
  }
}

export const googleVisionService = new GoogleVisionService();
